import React from 'react';
import { Icon } from '@iconify/react';
import { formatIntDate } from '../utils/dateUtils';

const getTypeColour = (type) => {
  if (!type) return 'secondary';
  const t = type.toLowerCase();
  if (t === 'advanced') return 'primary';
  if (t === 'standard') return 'success';
  if (t === 'basic') return 'secondary';
  return 'secondary';
};

const AssetDataTable = ({
  assets,
  useFunctionalLocation,
  meteredAssetIds = new Set(),
  planthireEnabled,
  onView,
  onEdit,
  onDelete,
  sortField,
  sortOrder,
  onSort,
  canEditAsset
}) => {
  const stickyHeaderCellStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: '#ffffff',
  };

  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return 'mdi:sort';
    }
    return sortOrder === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending';
  };

  const renderSortableHeader = (label, field) => {
    const ariaSort = sortField === field
      ? sortOrder === 'asc'
        ? 'ascending'
        : sortOrder === 'desc'
          ? 'descending'
          : 'none'
      : 'none';

    return (
      <th
        onClick={() => onSort?.(field)}
        style={{ ...stickyHeaderCellStyle, cursor: 'pointer' }}
        scope="col"
        aria-sort={ariaSort}
      >
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <Icon
            className="text-xl"
            icon={getSortIcon(field)}
          />
        </span>
      </th>
    );
  };

  return (
    <table className="table bordered-table mb-3 w-100 rounded-0">
      <thead>
        <tr>
          <th className="rounded-0" style={stickyHeaderCellStyle}></th>
          {renderSortableHeader('Asset', 'asset')}
          <th style={stickyHeaderCellStyle}>Status</th>
          {renderSortableHeader('Model or Group', 'group')}
          {renderSortableHeader('Class', 'class')}
          {useFunctionalLocation && <th style={stickyHeaderCellStyle}>FLOC</th>}
          {renderSortableHeader('Serial', 'serial')}
          <th style={stickyHeaderCellStyle}>Start Date</th>
          {planthireEnabled && <th style={stickyHeaderCellStyle}>Hire Status</th>}
          <th style={stickyHeaderCellStyle}>Type</th>
          {renderSortableHeader('Department', 'department')}
          <th className="Rounded-0" style={stickyHeaderCellStyle}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {assets.length === 0 && (
          <tr>
            <td colSpan="8" className="text-center py-4 rounded-0">No Assets Loaded</td>
          </tr>
        )}
        {assets.map((row, index) => {
          const placeholder = `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
          const imageUrl = row.assetImage
            ? `${process.env.REACT_APP_SPACES_BASE}/${row.assetImage}`
            : placeholder;
          const statusColour = row.plantStatus?.colours?.bootstrapName || 'secondary';
          const typeColour = getTypeColour(row.PlanningType?.typeDescription);
          return (
            // Combine the asset id with the index to ensure a unique key even if
            // duplicate ids are returned from the backend
            <tr key={`${row.id}-${index}`}>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <img
                    src={imageUrl}
                    alt="asset"
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '1px solid #ddd',
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = placeholder;
                    }}
                  />
                  {meteredAssetIds.has(row.id) && (
                    <Icon icon="mage:health-circle" className="text-secondary" />
                  )}
                </div>
              </td>
              <td>
                <strong>{row.assetNumber}</strong> | {row.assetDescription}
              </td>
              <td>
                {row.plantStatus && (
                  <span
                    className={`bg-${statusColour}-focus text-${statusColour}-main px-16 py-2 rounded-pill fw-medium text-sm`}
                  >
                    {row.plantStatus.status}
                  </span>
                )}
              </td>
              <td>{row.group?.groupName || ''}</td>
              <td>{row.group?.class?.className || ''}</td>
              {useFunctionalLocation && <td>{row.assetFloc}</td>}
              <td>{row.assetSerial}</td>
              <td>{formatIntDate(row.assetStartDate)}</td>
              {planthireEnabled && (
                <td>
                  {row.hireStatus === 1 ? (
                    <div className="badge bg-success-focus text-success">On Hire</div>
                  ) : row.hireStatus === 0 ? (
                    <div className="badge bg-danger-focus text-danger">Off Hire</div>
                  ) : null}
                </td>
              )}
              <td>
                {row.PlanningType && (
                  <span className={`badge bg-${typeColour}`}>
                    {row.PlanningType.typeDescription}
                  </span>
                )}
              </td>
              <td>{row.assetDepartment?.name || ''}</td>
              <td>
                {/*<button
                  className="view-btn w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                  onClick={() => onView(row)}
                >
                  <Icon icon="ic:baseline-visibility" />
                </button>*/}
                {canEditAsset && (<button
                  className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                  onClick={() => onEdit(row)}
                >
                  <Icon icon="ic:baseline-edit" />
                </button>)}
                <button
                  className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                  onClick={() => onDelete(row.id)}
                >
                  <Icon icon="ic:baseline-delete-outline" />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default AssetDataTable;
